import { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Box, 
  Typography, 
  Chip, 
  Pagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid
} from '@mui/material';
import { 
  Search, 
  FilterList, 
  Download, 
  Refresh, 
  ExpandMore, 
  ExpandLess 
} from '@mui/icons-material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

function LogFile() {
  const { t } = useAdminLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    action: '',
    userId: '',
    severity: '',
    startDate: '',
    endDate: '',
    reportNumber: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Chargement des logs
  const loadLogs = async (newPage = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: newPage.toString(),
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ''))
      });

      const response = await fetch(`/api/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des logs');
      }

      const data = await response.json();
      
      setLogs(data.data?.logs || []);
      setTotalPages(data.data?.pagination?.totalPages || 1);
      setTotal(data.data?.pagination?.total || 0);
      setPage(newPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Recherche dans les logs
  const searchLogs = async () => {
    if (!searchTerm.trim()) {
      loadLogs(1);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        q: searchTerm,
        page: '1',
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ''))
      });

      const response = await fetch(`/api/logs/search?${params}`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }

      const data = await response.json();
      
      setLogs(data.data?.logs || []);
      setTotalPages(data.data?.pagination?.totalPages || 1);
      setTotal(data.data?.pagination?.total || 0);
      setPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Export des logs
  const exportLogs = async () => {
    try {
      const params = new URLSearchParams({
        format: 'csv',
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ''))
      });

      const response = await fetch(`/api/logs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Utiliser la date locale pour le nom de fichier
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      a.download = `logs_${dateStr}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Erreur: ${err.message}`);
    }
  };

  // Formatage des données pour l'affichage
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'info': return 'primary';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'connexion': return 'info';
      case 'admin': return 'warning';
      case 'utilisateur': return 'success';
      case 'report': return 'primary';
      case 'erreur': return 'error';
      default: return 'default';
    }
  };

  // Basculer l'expansion des lignes
  const toggleRowExpansion = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  // Gestion du changement de page
  const handlePageChange = (event, newPage) => {
    loadLogs(newPage);
  };

  // Gestion du changement de filtres
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Remettre à la page 1 quand on change un filtre
    setPage(1);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      category: '',
      action: '',
      userId: '',
      severity: '',
      startDate: '',
      endDate: '',
      reportNumber: ''
    });
    setPage(1);
  };

  // Chargement initial
  useEffect(() => {
    loadLogs();
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    loadLogs(1); // Toujours charger la page 1 quand les filtres changent
  }, [filters]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('Journalisation (Logs)')}
      </Typography>
      
      <Info type="log" />

      {/* Barre de recherche et actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label={t('Rechercher')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && searchLogs()}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          onClick={searchLogs}
          startIcon={<Search />}
          disabled={loading}
        >
          {t('Rechercher')}
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={<FilterList />}
        >
          {t('Filtres')}
        </Button>
        <Button
          variant="outlined"
          onClick={() => loadLogs(page)}
          startIcon={<Refresh />}
          disabled={loading}
        >
          {t('Actualiser')}
        </Button>
        <Button
          variant="outlined"
          onClick={exportLogs}
          startIcon={<Download />}
        >
          {t('Exporter')}
        </Button>
        {Object.values(filters).some(value => value !== '') && (
          <Button
            variant="outlined"
            onClick={resetFilters}
            color="secondary"
          >
            {t('Réinitialiser filtres')}
          </Button>
        )}
      </Box>

      {/* Filtres */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('Filtres')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t('Catégorie')}</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <MenuItem value="">{t('Toutes')}</MenuItem>
                  <MenuItem value="connexion">Connexion</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="utilisateur">Utilisateur</MenuItem>
                  <MenuItem value="report">Report</MenuItem>
                  <MenuItem value="erreur">Erreur</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t('Sévérité')}</InputLabel>
                <Select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                >
                  <MenuItem value="">{t('Toutes')}</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Avertissement</MenuItem>
                  <MenuItem value="error">Erreur</MenuItem>
                  <MenuItem value="critical">Critique</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label={t('Matricule')}
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label={t('Date début')}
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label={t('Date fin')}
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label={t('N° Rapport')}
                value={filters.reportNumber}
                onChange={(e) => handleFilterChange('reportNumber', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Messages d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Compteur de résultats */}
      {!loading && total > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {total} {t('log(s) trouvé(s)')} - {t('Page')} {page} {t('sur')} {totalPages}
          </Typography>
        </Box>
      )}

      {/* Tableau des logs */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Date/Heure')}</TableCell>
              <TableCell>{t('Catégorie')}</TableCell>
              <TableCell>{t('Action')}</TableCell>
              <TableCell>{t('N° Rapport')}</TableCell>
              <TableCell>{t('Utilisateur')}</TableCell>
              <TableCell>{t('Sévérité')}</TableCell>
              <TableCell>{t('Détails')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('Chargement...')}
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('Aucun log trouvé')}
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <>
                  <TableRow key={log._id}>
                    <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={log.category} 
                        color={getCategoryColor(log.category)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>
                      {log.reportNumber && (
                        <Chip 
                          label={log.reportNumber} 
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {log.userId && (
                        <Box>
                          <Typography variant="body2">{log.userId}</Typography>
                          {log.userInfo && (
                            <Typography variant="caption" color="text.secondary">
                              {log.userInfo.firstname} {log.userInfo.lastname}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {log.adminMatricule && (
                        <Box>
                          <Typography variant="body2">{log.adminMatricule}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('Administrateur')}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.severity} 
                        color={getSeverityColor(log.severity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => toggleRowExpansion(log._id)}
                        size="small"
                      >
                        {expandedRows[log._id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {expandedRows[log._id] && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('Détails du log')}
                          </Typography>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(log.content, null, 2)}
                          </Typography>
                          {log.location && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              {t('Localisation')}: {log.location}
                            </Typography>
                          )}
                          {log.ipAddress && (
                            <Typography variant="caption" display="block">
                              IP: {log.ipAddress}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && logs.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {totalPages > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('Tous les résultats sont affichés sur cette page')}
              </Typography>
            </Box>
          )}
        </Box>
      )}


    </Box>
  );
}

export default LogFile;